import React, { useState, useEffect } from 'react';
import { dealsApi, leadsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  TrendingDown,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  ArrowRight
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Funnel,
  FunnelChart,
  LabelList
} from 'recharts';
import { toast } from 'sonner';

const STAGE_LABELS = {
  new: 'New',
  contacted: 'Contacted',
  booked: 'Booked',
  discovery_done: 'Discovery',
  proposal_draft: 'Proposal Draft',
  proposal_sent: 'Proposal Sent',
  closed_won: 'Won',
  closed_lost: 'Lost'
};

const STAGE_COLORS = {
  new: '#94A3B8',
  contacted: '#3B82F6',
  booked: '#8B5CF6',
  discovery_done: '#6366F1',
  proposal_draft: '#F59E0B',
  proposal_sent: '#F97316',
  closed_won: '#10B981',
  closed_lost: '#EF4444'
};

export default function Deals() {
  const navigate = useNavigate();
  const [pipeline, setPipeline] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDealsData();
  }, []);

  const loadDealsData = async () => {
    try {
      setLoading(true);
      const [pipelineRes, followUpsRes] = await Promise.all([
        dealsApi.getPipelineSummary(),
        dealsApi.getFollowUpDue()
      ]);
      setPipeline(pipelineRes.data);
      setFollowUps(followUpsRes.data.follow_ups_due || []);
    } catch (error) {
      toast.error('Failed to load deals data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="grid grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-slate-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const funnelData = pipeline?.funnel?.filter(f => 
    !['closed_won', 'closed_lost'].includes(f.stage)
  ).map(f => ({
    name: STAGE_LABELS[f.stage] || f.stage,
    value: f.count,
    fill: STAGE_COLORS[f.stage] || '#94A3B8'
  })) || [];

  return (
    <div className="p-8" data-testid="deals-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900">Deals Pipeline</h1>
        <p className="text-slate-500 mt-1">Track your sales funnel and deal progression</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card className="metric-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Leads</p>
                <p className="text-3xl font-bold tabular-nums">{pipeline?.total_leads || 0}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="metric-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Deals Won</p>
                <p className="text-3xl font-bold tabular-nums text-emerald-600">
                  {pipeline?.won || 0}
                </p>
              </div>
              <div className="p-3 bg-emerald-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="metric-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Deals Lost</p>
                <p className="text-3xl font-bold tabular-nums text-red-600">
                  {pipeline?.lost || 0}
                </p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="metric-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Win Rate</p>
                <p className="text-3xl font-bold tabular-nums">{pipeline?.win_rate || 0}%</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pipeline Funnel */}
        <Card className="col-span-2">
          <CardHeader>
            <CardTitle>Sales Funnel</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={funnelData} 
                  layout="vertical"
                  margin={{ left: 20, right: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" stroke="#94A3B8" fontSize={12} />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    stroke="#94A3B8" 
                    fontSize={12}
                    width={100}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar 
                    dataKey="value" 
                    radius={[0, 4, 4, 0]}
                  >
                    {funnelData.map((entry, index) => (
                      <rect key={index} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            {/* Pipeline Value */}
            <div className="mt-6 p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Total Pipeline Value</span>
                <span className="text-2xl font-bold">
                  {formatCurrency(pipeline?.total_pipeline_value)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Follow-ups Due */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              Follow-ups Due
            </CardTitle>
          </CardHeader>
          <CardContent>
            {followUps.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 text-emerald-300 mx-auto mb-4" />
                <p className="text-slate-500">All caught up!</p>
                <p className="text-sm text-slate-400">No follow-ups due</p>
              </div>
            ) : (
              <div className="space-y-3">
                {followUps.slice(0, 5).map((item, i) => (
                  <div 
                    key={i}
                    className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 
                               cursor-pointer transition-colors"
                    onClick={() => navigate(`/leads/${item.lead.id}`)}
                    data-testid={`followup-${item.lead.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{item.lead.name}</p>
                        <p className="text-xs text-slate-500">{item.lead.company}</p>
                      </div>
                      <div className="text-right">
                        <span className={`text-xs font-semibold ${
                          item.recommended_action === 'urgent_follow_up' 
                            ? 'text-red-600' 
                            : 'text-amber-600'
                        }`}>
                          {item.days_since_last_contact} days
                        </span>
                        <ArrowRight className="w-4 h-4 text-slate-400 ml-auto mt-1" />
                      </div>
                    </div>
                  </div>
                ))}
                
                {followUps.length > 5 && (
                  <Button 
                    variant="ghost" 
                    className="w-full text-slate-500"
                    onClick={() => navigate('/leads?stage=proposal_sent')}
                  >
                    View all ({followUps.length})
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Stage Breakdown */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Pipeline Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            {pipeline?.funnel?.map((stage, i) => (
              <div 
                key={i}
                className="p-4 rounded-lg text-center cursor-pointer hover:shadow-md transition-shadow"
                style={{ backgroundColor: `${STAGE_COLORS[stage.stage]}20` }}
                onClick={() => navigate(`/leads?stage=${stage.stage}`)}
                data-testid={`stage-${stage.stage}`}
              >
                <p className="text-2xl font-bold tabular-nums" style={{ color: STAGE_COLORS[stage.stage] }}>
                  {stage.count}
                </p>
                <p className="text-xs text-slate-600 mt-1">
                  {STAGE_LABELS[stage.stage] || stage.stage}
                </p>
                {stage.value > 0 && (
                  <p className="text-xs text-slate-400 mt-1">
                    {formatCurrency(stage.value)}
                  </p>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
